/* eslint-disable import/no-unresolved */
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import CookiesConsent from 'components/CookiesConsent'
import ModalPrescriberTerms from 'components/ModalPrescriberTerms'
import { useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { BrandProvider } from './contexts/BrandContext'
import AppProvider from './hooks'
import useMediaQuery from './hooks/useMediaQuery'
import { queryClient } from './query-client'
import Routes from './routes'
import { MOBILE_WIDTH } from './utils/constants'

if (import.meta.env.VITE_APP_ENV === 'production') {
  Sentry.init({
    dsn: 'https://5847ca586bf3cf52c09a68e9d7aebbd8@o4506678724198400.ingest.us.sentry.io/4506678744645632',
    integrations: [Sentry.browserTracingIntegration()],
    // Performance Monitoring
    tracesSampleRate: 0.1, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['https://www.cannect.life/']
    // Session Replay
    // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    // replaysOnErrorSampleRate: 1.0
  })
}

function App() {
  const [showAlert, setShowAlert] = useState(false)

  // O GA4 e carregado pelo container GTM (GTM-5ZTKGJF), que dispara a tag de
  // G-CL2XYTV96X em todas as paginas. Nao inicialize o measurement id aqui:
  // duas instancias do mesmo id duplicam page_view.

  const isMobile = useMediaQuery(MOBILE_WIDTH)

  const contextClass = {
    success: 'bg-primary-400 text-primary',
    error: 'bg-error-700 text-white',
    info: 'bg-blue-500 text-white',
    warning: 'bg-orange-400 text-white',
    default: 'bg-gray-50 text-primary'
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <BrandProvider>
            <AppProvider>
              {showAlert && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
                  <div className="relative w-full max-w-md rounded-xl bg-white shadow-2xl">
                    <div className="flex items-start justify-between rounded-t-xl bg-amber-50 px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">⚠️</span>
                        <h2 className="text-base font-semibold leading-snug text-amber-800">
                          Instabilidade nos canais de atendimento
                        </h2>
                      </div>
                      <button
                        onClick={() => setShowAlert(false)}
                        className="ml-2 mt-0.5 flex-shrink-0 border-none bg-transparent text-amber-600 hover:text-amber-900"
                        aria-label="Fechar">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="px-5 py-4 text-sm leading-relaxed text-gray-700">
                      <p className="mb-3">
                        Aplicativos e serviços da Meta, incluindo <strong>WhatsApp</strong>, <strong>Instagram</strong>{' '}
                        e <strong>Facebook</strong>, apresentam instabilidade nesta sexta-feira, 12 de junho.
                      </p>
                      <p className="mb-3">
                        Por esse motivo, nosso atendimento pode sofrer{' '}
                        <strong>atrasos ou falhas temporárias de acesso</strong>.
                      </p>
                      <p>Estamos acompanhando a situação e seguimos à disposição pelos canais disponíveis.</p>
                    </div>
                    <div className="flex justify-end rounded-b-xl border-t border-gray-100 px-5 py-3">
                      <button
                        onClick={() => setShowAlert(false)}
                        className="rounded-lg border-none bg-amber-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-600">
                        Entendido
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <ToastContainer
                // Acima do modal da trilha (z-[1000001]); senão o aviso fica atrás do desfoque.
                style={{ zIndex: 1000002 }}
                toastClassName={(context) =>
                  `${contextClass[context?.type || 'default']} mb-4 rounded-2xl shadow-md font-semibold font-sans min-h-14 md:min-h-20 flex items-center justify-center gap-2 px-4 py-2 m-2 z-[50]`
                }
                position={isMobile ? 'bottom-center' : 'top-right'}
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                theme="colored"
                pauseOnHover
              />
              <Routes />
              <CookiesConsent />
              <ModalPrescriberTerms />
              {/* <ActiveCampaingModal /> */}
            </AppProvider>
          </BrandProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </HelmetProvider>
  )
}

export default App
